import { useCallback, useEffect, useState } from "react";
import { Share2, Download, Copy, Check } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useAuth } from "@/context/AuthContext";

const W = 1080;
const H = 1080;
const TONE = { confident: "#34D399", "getting there": "#22B8FF", "starting out": "#FBBF24" };

const loadLogo = () =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = "/logo.png";
  });

// A square card of every language, its level and the practice streak — with the referral link.
export const ShareProgress = ({ open, onOpenChange, languages = [], streak = 0, week }) => {
  const { user } = useAuth();
  const [url, setUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  const link = `${window.location.origin}/signup?ref=${user?.referral_code || ""}`;
  const best = languages[0];
  const text = best
    ? `My ${best.name} is ${best.level} on ChatMiLu — ${streak} day practice streak. Learn a language by talking to MiLu:`
    : `I am learning languages by talking to MiLu on ChatMiLu:`;

  const draw = useCallback(async () => {
    const canvas = document.createElement("canvas");
    canvas.width = W;
    canvas.height = H;
    const ctx = canvas.getContext("2d");

    ctx.fillStyle = "#0A0A0B";
    ctx.fillRect(0, 0, W, H);
    const glow = ctx.createRadialGradient(140, 120, 40, 140, 120, 900);
    glow.addColorStop(0, "rgba(26,125,255,0.26)");
    glow.addColorStop(1, "rgba(10,10,11,0)");
    ctx.fillStyle = glow;
    ctx.fillRect(0, 0, W, H);

    ctx.strokeStyle = "rgba(255,255,255,0.10)";
    ctx.lineWidth = 3;
    const r = 56;
    ctx.beginPath();
    ctx.moveTo(48 + r, 48);
    ctx.arcTo(W - 48, 48, W - 48, H - 48, r);
    ctx.arcTo(W - 48, H - 48, 48, H - 48, r);
    ctx.arcTo(48, H - 48, 48, 48, r);
    ctx.arcTo(48, 48, W - 48, 48, r);
    ctx.closePath();
    ctx.stroke();

    ctx.textAlign = "left";
    ctx.fillStyle = "#1A7DFF";
    ctx.font = "700 32px Helvetica, Arial, sans-serif";
    ctx.fillText("MY LANGUAGES", 108, 168);
    ctx.fillStyle = "#ECEEF1";
    ctx.font = "800 76px Helvetica, Arial, sans-serif";
    ctx.fillText(user?.name?.split(" ")[0] || "Me", 108, 254);

    let y = 360;
    languages.slice(0, 4).forEach((l) => {
      ctx.fillStyle = "rgba(255,255,255,0.05)";
      ctx.beginPath();
      ctx.roundRect(108, y - 58, W - 216, 108, 26);
      ctx.fill();
      ctx.fillStyle = "#ECEEF1";
      ctx.font = "700 48px Helvetica, Arial, sans-serif";
      ctx.fillText(l.name, 148, y - 2);
      ctx.fillStyle = TONE[l.level] || "#9AA1AB";
      ctx.font = "600 30px Helvetica, Arial, sans-serif";
      ctx.fillText(l.level.toUpperCase(), 148, y + 36);
      ctx.textAlign = "right";
      ctx.fillStyle = "#ECEEF1";
      ctx.font = "800 54px Helvetica, Arial, sans-serif";
      ctx.fillText(`${l.best}`, W - 152, y + 8);
      ctx.fillStyle = "#6B7280";
      ctx.font = "500 24px Helvetica, Arial, sans-serif";
      ctx.fillText("best score", W - 152, y + 40);
      ctx.textAlign = "left";
      y += 134;
    });

    ctx.fillStyle = "#FB923C";
    ctx.font = "700 38px Helvetica, Arial, sans-serif";
    ctx.fillText(`${streak} day practice streak`, 108, Math.min(y + 34, 902));
    if (week?.goal) {
      ctx.fillStyle = "#9AA1AB";
      ctx.font = "500 30px Helvetica, Arial, sans-serif";
      ctx.fillText(`${week.done} of ${week.goal} practice calls this week`, 108,
                   Math.min(y + 80, 944));
    }

    const logo = await loadLogo();
    ctx.fillStyle = "#1A7DFF";
    ctx.font = "600 36px Helvetica, Arial, sans-serif";
    ctx.fillText("Learned by talking, not tapping", 108, H - 240);
    if (logo) {
      const h = 60;
      const w = (logo.width / logo.height) * h;
      ctx.drawImage(logo, 108, H - 168, w, h);
    } else {
      ctx.fillStyle = "#ECEEF1";
      ctx.font = "800 42px Helvetica, Arial, sans-serif";
      ctx.fillText("ChatMiLu", 108, H - 120);
    }
    ctx.fillStyle = "#6B7280";
    ctx.font = "500 28px Helvetica, Arial, sans-serif";
    ctx.fillText("chatmilu.com", 108, H - 86);

    canvas.toBlob((b) => {
      if (b) setUrl(URL.createObjectURL(b));
      else console.error("progress card: toBlob returned nothing");
    }, "image/png");
  }, [languages, streak, week, user?.name]);

  useEffect(() => {
    if (open) draw().catch((e) => console.error("progress card draw failed", e));
  }, [open, draw]);

  const share = async () => {
    const body = `${text} ${link}`;
    try {
      const res = await fetch(url);
      const file = new File([await res.blob()], "chatmilu-progress.png", { type: "image/png" });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], text: body });
        return;
      }
      if (navigator.share) {
        await navigator.share({ text: body });
        return;
      }
      await navigator.clipboard.writeText(body);
      toast.success("Message and link copied — paste it anywhere");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not open the share sheet");
    }
  };

  const copyLink = async () => {
    await navigator.clipboard.writeText(`${text} ${link}`);
    setCopied(true);
    setTimeout(() => setCopied(false), 2200);
    toast.success("Copied — your referral link is included");
  };

  const download = () => {
    const a = document.createElement("a");
    a.href = url;
    a.download = "chatmilu-progress.png";
    a.click();
  };

  const social = (href) => window.open(href, "_blank", "noopener,noreferrer");

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="share-progress-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-xl font-bold tracking-tight">
            Share your progress
          </DialogTitle>
        </DialogHeader>

        {url && (
          <img src={url} alt="Your progress card" data-testid="share-progress-preview"
               className="w-full rounded-2xl border border-white/10" />
        )}
        <p className="text-xs text-ink-soft">
          The link carries your referral code — when a friend joins, you both get Premium days.
        </p>

        <div className="grid grid-cols-2 gap-2">
          <Button data-testid="share-progress-share" onClick={share}
                  className="min-h-[44px] rounded-full bg-brand hover:bg-brand-hover">
            <Share2 className="me-2 h-4 w-4" /> Share
          </Button>
          <Button data-testid="share-progress-download" variant="outline" onClick={download}
                  disabled={!url} className="min-h-[44px] rounded-full">
            <Download className="me-2 h-4 w-4" /> Save image
          </Button>
          <Button
            data-testid="share-progress-whatsapp"
            variant="outline"
            onClick={() => social(`https://wa.me/?text=${encodeURIComponent(`${text} ${link}`)}`)}
            className="min-h-[44px] rounded-full"
          >
            WhatsApp
          </Button>
          <Button
            data-testid="share-progress-x"
            variant="outline"
            onClick={() => social(
              `https://twitter.com/intent/tweet?text=${encodeURIComponent(text)}&url=${encodeURIComponent(link)}`)}
            className="min-h-[44px] rounded-full"
          >
            Post on X
          </Button>
        </div>
        <Button data-testid="share-progress-copy" variant="ghost" onClick={copyLink}
                className="min-h-[40px] rounded-full text-ink-soft">
          {copied ? <Check className="me-2 h-4 w-4" /> : <Copy className="me-2 h-4 w-4" />}
          Copy message and link
        </Button>
      </DialogContent>
    </Dialog>
  );
};
